/**
 * ConversationHeaderTitle — the title block inside a conversation's header.
 *
 * Lifted out of `ConversationPanel` so the White-Label preview can render the
 * real thing. Only the TITLE is extracted, not the whole header: the rest of
 * that header is the bot-info card and the debug button, both of which are
 * bound to the panel's own state and to network data. Pulling those out too
 * would mean threading a dozen props through for no gain — the preview does
 * not show them, and the page would be harder to read.
 *
 * Which branch renders is the part that matters for fidelity, so the decision
 * lives here rather than at the call site:
 *   • a named agent → a clickable name button (or a plain span when the name
 *     is a branch label, which is static text);
 *   • no agent → the generic chat coin plus the conversation title.
 */

interface Props {
  /** The agent name, branch label, or conversation title. */
  displayName?: string | null;
  /** True when `displayName` is a split-view branch label rather than an
   *  agent name — labels are never clickable. */
  isPanelLabel?: boolean;
  /** Opens the bot-info card. Absent → the name renders as a plain span. */
  onOpenInfo?: () => void;
  infoTitle?: string;
  /** Shown when there is no agent at all. */
  fallbackTitle: string;
  /** Swaps the coin glyph for the split/branch icon. */
  branchIcon?: boolean;
}

export default function ConversationHeaderTitle({
  displayName,
  isPanelLabel,
  onOpenInfo,
  infoTitle,
  fallbackTitle,
  branchIcon,
}: Props) {
  if (displayName) {
    return onOpenInfo && !isPanelLabel ? (
      <button
        type="button"
        className="chat-header-bot-name chat-header-bot-name-btn"
        onClick={onOpenInfo}
        title={infoTitle}
      >
        {displayName}
      </button>
    ) : (
      <span className={`chat-header-bot-name ${isPanelLabel ? 'is-panel-label' : ''}`}>
        {displayName}
      </span>
    );
  }

  return (
    <>
      <span className="chat-header-bot-avatar chat-header-bot-avatar-general">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {branchIcon ? (
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          ) : (
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          )}
        </svg>
      </span>
      <span className="chat-header-bot-name">{fallbackTitle}</span>
    </>
  );
}
